/**
 * Centralized AI helper for public tool pages.
 *
 * All AI config (provider, model, API key) comes from SSM via siteConfig.
 * To switch providers, update SSM params — no code changes needed.
 */

import { DEEPSEEK_API_KEY, DEEPSEEK_MODEL } from "./siteConfig";

const DEEPSEEK_URL = "https://api.deepseek.com/chat/completions";

export interface AiOptions {
  /** The prompt to send */
  prompt: string;
  /** Max tokens in response (default: 1000) */
  maxTokens?: number;
}

/**
 * Call the DeepSeek chat API and return the raw text response.
 * Throws on HTTP errors with a descriptive message.
 */
export async function callAI({ prompt, maxTokens = 1000 }: AiOptions): Promise<string> {
  const res = await fetch(DEEPSEEK_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${DEEPSEEK_API_KEY}`,
    },
    body: JSON.stringify({
      model: DEEPSEEK_MODEL,
      max_tokens: maxTokens,
      messages: [{ role: "user", content: prompt }],
    }),
  });

  if (!res.ok) {
    const e = await res.json().catch(() => ({})) as { error?: { message?: string } };
    throw new Error(e?.error?.message ?? `AI service error (${res.status})`);
  }

  const data = await res.json() as {
    choices?: Array<{ message?: { content?: string } }>;
  };

  return (data.choices?.[0]?.message?.content ?? "").trim();
}

/**
 * Call the AI and parse the response as JSON.
 * Strips markdown code fences if present.
 */
export async function callAIJson<T>(opts: AiOptions): Promise<T> {
  const raw = await callAI(opts);
  const cleaned = raw.replace(/```json\s*/g, "").replace(/```\s*/g, "").trim();
  return JSON.parse(cleaned) as T;
}
